import { useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import { scrollElementIntoView } from "@lib/helpers";
import PropTypes from "prop-types";
import { Field, Form, FormSpy } from "react-final-form";
import classNames from "classnames";
import getConfig from "next/config";

import Price from "@components/Price";
import AddToCart from "@containers/Catalog/Actions/AddToCart";
import Heading from "@components/Heading";
import ArrowDown from "@components/Icons/ArrowDown";
import Guaranty from "@components/Icons/Resealed/Guaranty";
import Return from "@components/Icons/Resealed/Return";
import Tested from "@components/Icons/Resealed/Tested";
import ModalErrorBoundary from "@containers/ModalErrorBoundary";
import ResealedProductStorePickupModal from "@components/ResealedProductStorePickupModal";
import AddToCartReserveInStore from "@components/AddToCartReserveInStore";
import { addModal } from "@store/app/actions";
import Select from "@components/Form/Select";
import ProductBenefits from "@components/ProductPage/ProductBenefits";

const {
  publicRuntimeConfig: {
    settings: { websiteName },
  },
} = getConfig();

const isBricoStore = websiteName === "bricostore";

const INITIAL_VISIBLE_ITEMS = 2;

const ResealedList = ({ product, addModal }) => {
  const [visibleResealedItems, setVisibleResealedItems] = useState(
    INITIAL_VISIBLE_ITEMS
  );
  const [filters, setFilters] = useState({
    region: "",
    store: "",
    price: "asc",
  });

  const resealedInfoRef = useRef();

  const availableResealedRegionNames = [
    ...new Set(
      product.resealed.map(
        (resealedItem) =>
          resealedItem.stock.store_availability_location.region_name
      )
    ),
  ];

  const filteredResealedProducts = useMemo(() => {
    let validResealedProducts = product.resealed.filter(
      (resealedItem) => resealedItem.sku !== product.sku
    );

    if (filters.region) {
      validResealedProducts = validResealedProducts.filter(
        (resealedItem) =>
          resealedItem.stock.store_availability_location.region_name ===
          filters.region
      );
    }

    if (filters.store) {
      validResealedProducts = validResealedProducts.filter(
        (resealedItem) =>
          resealedItem.stock.store_availability_location.name === filters.store
      );
    }

    if (validResealedProducts.length > 1) {
      validResealedProducts = validResealedProducts.sort((a, b) =>
        filters.price === "asc"
          ? a.price.selling_price - b.price.selling_price
          : b.price.selling_price - a.price.selling_price
      );
    }

    return validResealedProducts;
  }, [filters]);

  const availableResealedStoreNames = useMemo(() => {
    const source = filters.region
      ? product.resealed.filter(
          (item) =>
            item.stock.store_availability_location.region_name ===
            filters.region
        )
      : product.resealed;

    return [
      ...new Set(
        source.map((item) => item.stock.store_availability_location.name)
      ),
    ];
  }, [filters.region, product.resealed, filteredResealedProducts]);

  const resealedItemsCount = filteredResealedProducts.length;

  const showMoreItems = (event) => {
    event.preventDefault();

    setVisibleResealedItems((prevState) => {
      // Show 5
      if (prevState === 2) {
        return resealedItemsCount > 5 ? 5 : resealedItemsCount;
      }

      // Show 10
      if (prevState === 5) {
        return resealedItemsCount > 10 ? 10 : resealedItemsCount;
      }

      // Show all
      return resealedItemsCount;
    });
  };

  const collapseList = (event) => {
    event.preventDefault();

    setVisibleResealedItems(INITIAL_VISIBLE_ITEMS);

    setTimeout(() => {
      scrollElementIntoView(resealedInfoRef.current, "smooth");
    }, 50);
  };

  const handleResealedFilterChange = (form) => {
    const filterValues = form.getState().values;

    if (filters.region !== filterValues.region) {
      form.change("store", "");
    }

    setVisibleResealedItems(INITIAL_VISIBLE_ITEMS);
    setFilters(filterValues);
  };

  return (
    <>
      <div className="mb-5 flow-root" ref={resealedInfoRef}>
        <div className="flex text-xl m-0">
          <Heading
            as="h2"
            display="h2"
            title="Produse Resigilate"
            classesProp="mb-4"
          />
        </div>

        <div className="text-md md:space-x-7 lg:space-x-8 md:float-left md:flex w-full text-green">
          <div className="flex mb-10px md:mb-0">
            <Guaranty />
            <span className="pl-6px text-sm lg:text-base leading-18.2 lg:leading-20.8">
              Garantia produsului nou
            </span>
          </div>

          <div className="flex mb-10px md:mb-0">
            <Return />
            <span className="pl-6px text-sm lg:text-base leading-18.2 lg:leading-20.8">
              {isBricoStore ? "Retur gratuit in 30 zile" : "Retur gratuit in 14 zile"}
            </span>
          </div>

          <div className="flex">
            <Tested />
            <span className="pl-6px text-sm lg:text-base leading-18.2 lg:leading-20.8">
              Produsul a fost testat
            </span>
          </div>
        </div>
      </div>

      {product.resealed.length > 5 && (
        <Form
          onSubmit={() => null}
          initialValues={{ price: "asc", region: null, store: null }}
          render={({ form }) => (
            <form className="space-y-[10px] md:grid md:grid-cols-3 lg:max-w-[80%] md:space-y-0 md:mb-[20px] gap-[10px]">
              <FormSpy
                onChange={() => handleResealedFilterChange(form)}
                subscription={{ values: true }}
              />

              <Field
                component={Select}
                name="region"
                inputClassName="!max-w-full"
                parse={(value) => value}
              >
                <option value="">Judet</option>

                {availableResealedRegionNames.map((region, index) => (
                  <option key={index} value={region}>
                    {region}
                  </option>
                ))}
              </Field>

              <Field
                component={Select}
                name="store"
                inputClassName="!max-w-full"
                parse={(value) => value}
              >
                <option value="">Alege magazin</option>

                {availableResealedStoreNames.map((store, index) => (
                  <option key={index} value={store}>
                    {store}
                  </option>
                ))}
              </Field>

              <Field
                component={Select}
                name="price"
                inputClassName="!max-w-full"
                parse={(value) => value}
              >
                <option value="asc">Pret crescator</option>
                <option value="desc">Pret descrescator</option>
              </Field>
            </form>
          )}
        />
      )}

      <ul
        className={`
            m-0 p-0 border-gray-1100 md:border md:rounded-tr-xl md:rounded-bl-xl
            ${
              resealedItemsCount <= visibleResealedItems
                ? "mb-22px lg:mb-52px"
                : "mb-23px"
            }
          `}
      >
        {filteredResealedProducts
          .filter((item, index) => visibleResealedItems >= index + 1)
          .map((resealedItem, index) => (
            <li
              key={index}
              className="p-4 lg:p-20px lg:flex items-center resealed border-gray-1100 border md:border-0 rounded-tr-xl rounded-bl-xl md:rounded-none mt-4 md:mt-0"
            >
              <div className="lg:w-7/12 sm:flex items-center">
                <div className="pb-2 lg:pb-0 sm:pr-2 sm:w-1/2">
                  <div className="pl-0">
                    {resealedItem.resealed_reasons &&
                    resealedItem.resealed_reasons.length > 0 ? (
                      resealedItem.resealed_reasons.map(
                        (resealedReason, index) => (
                          <div key={index}>{resealedReason}.</div>
                        )
                      )
                    ) : (
                      <div>Produsul a fost desigilat</div>
                    )}
                  </div>
                </div>

                <div className="pb-2 lg:pb-0 sm:pl-2 sm:w-1/2">
                  {product?.resealed_label?.seller_ids &&
                    product.resealed_label.seller_ids.includes(
                      resealedItem.seller.id
                    ) && (
                      <div className="mb-2">
                        <ProductBenefits benefits={[product.resealed_label]} />
                      </div>
                    )}
                  {resealedItem.stock.store_availability_location && (
                    <>
                      Disponibil in
                      <br />
                      <strong className="uppercase">
                        {resealedItem.stock.store_availability_location.name}
                      </strong>
                      <br />
                      {resealedItem.stock.store_availability_location.address}
                      <br />
                      {
                        resealedItem.stock.store_availability_location
                          .region_name
                      }
                      <br />
                    </>
                  )}
                </div>
              </div>

              <div className="lg:w-5/12 lg:pl-8 flex flex-row items-center space-x-5 sm:space-x-0 justify-between">
                <Price
                  price={resealedItem.price.selling_price}
                  regularPrice={resealedItem.price.base_price}
                  formatted={true}
                  size="productPage"
                  resealed={true}
                  resealedLabel="Pret produs nou"
                  displayDiscount={true}
                />

                <div className="flex flex-row-reverse sm:flex-col gap-x-3 sm:gap-x-0 sm:gap-y-2 sm:w-[162px] lg:w-[232px]">
                  {resealedItem.stock && resealedItem.stock.status === 1 && (
                    <AddToCart
                      iconOnlyOnMobile
                      isCatalog={true}
                      product={{
                        is_resealed: 1,
                        mainOffer: 0,
                        offers: [resealedItem],
                        name: product.name,
                        sku: resealedItem.sku,
                        resealed_code: resealedItem.resealed_code,
                        image: product.image,
                        resealed_reasons: resealedItem.resealed_reasons,
                      }}
                    />
                  )}

                  {resealedItem.stock &&
                    resealedItem.stock.status === 0 &&
                    resealedItem.stock.available_in_store === 1 && (
                      <AddToCartReserveInStore
                        onPostSuccess={() =>
                          addModal(`resealedProductStorePickup_${index}`)
                        }
                        product={resealedItem}
                        iconOnlyOnMobile
                      />
                    )}

                  <ModalErrorBoundary>
                    <ResealedProductStorePickupModal
                      mainProduct={product}
                      resealedProduct={resealedItem}
                      resealedModalId={`resealedProductStorePickup_${index}`}
                    />
                  </ModalErrorBoundary>
                </div>
              </div>
            </li>
          ))}
      </ul>

      {resealedItemsCount > INITIAL_VISIBLE_ITEMS && (
        <div
          className={classNames("flex justify-center mb-[22px] lg:mb-[52px]", {
            "text-bleu": websiteName === "bricostore",
            "text-black": websiteName !== "bricostore",
          })}
        >
          <a
            href=""
            className="flex items-center"
            onClick={
              visibleResealedItems === resealedItemsCount
                ? collapseList
                : showMoreItems
            }
          >
            {visibleResealedItems === resealedItemsCount
              ? "Restrange lista"
              : "Vezi mai multe produse resigilate"}

            <span className="ml-2">
              <ArrowDown
                fill="currentColor"
                customClassName={`duration-75 ${
                  visibleResealedItems === resealedItemsCount
                    ? "rotate-180"
                    : ""
                }`}
              />
            </span>
          </a>
        </div>
      )}
    </>
  );
};

ResealedList.propTypes = {
  product: PropTypes.object.isRequired,
};

export default connect(null, { addModal })(ResealedList);
